import React from 'react';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  AutoAwesome as BriefingIcon,
  SentimentVerySatisfied as SentPositiveIcon,
  SentimentSatisfied as SentNeutralIcon,
  SentimentVeryDissatisfied as SentNegativeIcon,
  Star as ExposureDedicatedIcon,
  Groups as ExposureSharedIcon,
  FormatQuote as ExposureMentionIcon
} from '@mui/icons-material';
import {
  colors,
  operationalRadii,
  operationalStatusColors
} from '../../../../design-system/tokens';

const sectionLabelSx = {
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  color: 'text.secondary'
};

const menuPaperSx = {
  borderRadius: `${operationalRadii.surface}px`
};

const SectionLabel = ({ children }) => (
  <MenuItem dense disableRipple sx={{ opacity: 0.5, cursor: 'default', pointerEvents: 'none', minHeight: 26, px: 2 }}>
    <ListItemText primaryTypographyProps={sectionLabelSx}>
      {children}
    </ListItemText>
  </MenuItem>
);

SectionLabel.propTypes = {
  children: PropTypes.node.isRequired
};

const ArticleSentimentMenu = ({
  anchorEl,
  currentValue,
  analyzing,
  onClose,
  onSelect,
  onAnalyze
}) => {
  const options = [
    { value: 'positive', label: 'Positivo', Icon: SentPositiveIcon, color: colors.sentiment.positive.main },
    { value: 'neutral', label: 'Neutro', Icon: SentNeutralIcon, color: 'grey.500' },
    { value: 'negative', label: 'Negativo', Icon: SentNegativeIcon, color: colors.sentiment.negative.main },
  ];

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { ...menuPaperSx, minWidth: 190 } }}
    >
      <SectionLabel>Manual</SectionLabel>
      {options.map(({ value, label, Icon, color }) => (
        <MenuItem key={value} onClick={() => onSelect(value)}>
          <ListItemIcon>
            <Icon sx={{ fontSize: 22, color, opacity: currentValue === value ? 1 : 0.4 }} />
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontWeight: currentValue === value ? 700 : 400,
              color: currentValue === value ? color : 'text.primary'
            }}
          />
        </MenuItem>
      ))}
      <Divider sx={{ my: 0.5 }} />
      <SectionLabel>MIA (IA)</SectionLabel>
      <MenuItem onClick={onAnalyze} disabled={analyzing}>
        <ListItemIcon>
          {analyzing ? <CircularProgress size={18} /> : <AutoAwesomeIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
        </ListItemIcon>
        <ListItemText
          primary={analyzing ? 'Analisando...' : 'Analisar com IA'}
          primaryTypographyProps={{ color: 'primary.main' }}
        />
      </MenuItem>
    </Menu>
  );
};

const ArticleExposureMenu = ({
  anchorEl,
  currentValue,
  analyzing,
  onClose,
  onSelect,
  onAnalyze
}) => {
  const options = [
    { value: 'dedicated', label: 'Dedicada', Icon: ExposureDedicatedIcon, color: operationalStatusColors.success },
    { value: 'shared', label: 'Compartilhada', Icon: ExposureSharedIcon, color: operationalStatusColors.info },
    { value: 'mention', label: 'Citação', Icon: ExposureMentionIcon, color: 'grey.500' },
  ];

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { ...menuPaperSx, minWidth: 210 } }}
    >
      <SectionLabel>Manual</SectionLabel>
      {options.map(({ value, label, Icon, color }) => (
        <MenuItem key={value} onClick={() => onSelect(value)}>
          <ListItemIcon>
            <Icon sx={{ fontSize: 22, color, opacity: currentValue === value ? 1 : 0.4 }} />
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontWeight: currentValue === value ? 700 : 400,
              color: currentValue === value ? color : 'text.primary'
            }}
          />
        </MenuItem>
      ))}
      <Divider sx={{ my: 0.5 }} />
      <SectionLabel>MIA (IA)</SectionLabel>
      <MenuItem onClick={onAnalyze} disabled={analyzing}>
        <ListItemIcon>
          {analyzing ? <CircularProgress size={18} /> : <BriefingIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
        </ListItemIcon>
        <ListItemText
          primary={analyzing ? 'Analisando...' : 'Analisar com IA'}
          primaryTypographyProps={{ color: 'primary.main' }}
        />
      </MenuItem>
    </Menu>
  );
};

const menuPropTypes = {
  anchorEl: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func
  ]),
  currentValue: PropTypes.string,
  analyzing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onAnalyze: PropTypes.func.isRequired
};

const menuDefaultProps = {
  anchorEl: null,
  currentValue: ''
};

ArticleSentimentMenu.propTypes = menuPropTypes;
ArticleSentimentMenu.defaultProps = menuDefaultProps;
ArticleExposureMenu.propTypes = menuPropTypes;
ArticleExposureMenu.defaultProps = menuDefaultProps;

export {
  ArticleSentimentMenu,
  ArticleExposureMenu
};
